import React, { useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider } from 'react-query';

import { CommandList, defaultKeyboardShortcuts } from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';

import { routesConfig } from './routesConfig';
import { ErrorBoundary } from './components/ErrorBoundary';
import ActivityRail from './components/ActivityRail';
import OfflineBanner from './components/OfflineBanner';
import HelpTextProvider from './components/HelpTextProvider';
import Dashboard from './routes/Dashboard';
import FrontPageRedirect from './components/FrontPageRedirect';
import AccessDenied from './components/AccessDenied';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

const CznaAuthoritiesApp = ({ match }) => {
  const stripes = useStripes();
  useEffect(() => {
    if (stripes.timezone === 'UTC') stripes.setTimezone('Europe/Prague');
  }, [stripes]);

  // NOTE (TASK-180 pivot 2026-06-01): FOLIO Stripes Settings mount was
  // removed per project rule — settings UI lives as a regular in-module
  // route (`/czna-authorities/settings`) registered in `routesConfig` and
  // surfaced via the persistent left `ActivityRail`.
  //
  // CZNA-314: navigation is a stable activity bar. `ActivityRail` is rendered
  // ONCE here, outside the route `Switch`, so the top-level menu (Přehled / the
  // four sections / Ke schválení / ⚙ Nastavení) is always visible and never
  // drills away. The route content fills the positioned wrapper to its right —
  // each route's `<Paneset>` is `position:absolute; size:100%`, so it fills the
  // relative flex child. The module root renders the `Dashboard` (was: redirect
  // to /proposals); unknown sub-paths fall back to the dashboard.
  //
  // CZNA-1624: the module root is a column — the offline bar on top, IN the
  // document flow, and the rail + route content below it taking the remaining
  // height (`flex: 1 1 0; min-height: 0`). While offline the content shrinks by the
  // bar's height instead of being overlaid, so the pane footers (Uložit/Zrušit)
  // stay visible and clickable. Rendered once here, so it is on every screen.
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <HelpTextProvider>
          <CommandList commands={defaultKeyboardShortcuts}>
            <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <OfflineBanner />
              <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex' }}>
                <ActivityRail />
                <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  <Switch>
                    {/* CZNA-1440 / CZNA-1627: a route the user may not open stays registered
                        but renders AccessDenied in place of its component — the URL stays and
                        explains itself, and the protected screen never mounts, so none of its
                        API reads run. Only unknown sub-paths fall through to the redirect below. */}
                    {Object.values(routesConfig).map((route) => (
                      <Route
                        key={route.path}
                        path={`${match.path}${route.path}`}
                        render={(routeProps) => (
                          !route.perm || stripes.hasPerm(route.perm)
                            ? <route.component {...routeProps} />
                            : <AccessDenied homePath={match.path} />
                        )}
                      />
                    ))}
                    <Route exact path={match.path} component={Dashboard} />
                    <Redirect to={match.path} />
                  </Switch>
                </div>
              </div>
            </div>
          </CommandList>
        </HelpTextProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

CznaAuthoritiesApp.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
};

// CZNA-1424: the module also acts as the `frontpage` plugin, which stripes-core mounts on `/`
// with `actAs="plugin"` and no `match` — that render replaces the FOLIO welcome page.
const CznaAuthorities = (props) => (
  props.actAs === 'plugin' ? <FrontPageRedirect /> : <CznaAuthoritiesApp {...props} />
);

CznaAuthorities.propTypes = {
  actAs: PropTypes.string,
};

export default CznaAuthorities;
